import { useEffect } from "react";
import { useSearchParams } from "react-router";

import type { ExtractDebugRecord } from "@prequel/import-react";
import { useExtractDebug } from "@prequel/import-react";
import { Alert, AlertKind, Spinner, type TableRow } from "@prequel/shared-components";

import ResourceTable, { type FilterField } from "@/components/ResourceTable";
import Timestamp from "@/components/Timestamp";
import { useExtractContext } from "@/pages/import/DatasetsPage/ExtractPage";

const COLUMNS = ["Record ID", "Last Modified in Source", "Error"];

const FILTER_FIELDS: FilterField[] = [
  {
    key: "has_error",
    label: "Errors",
    queryParam: "has_error",
    options: [
      { value: "true", label: "Errors only" },
      { value: "false", label: "No errors" },
    ],
    radio: true,
  },
];

const mapRecordToRow = (record: ExtractDebugRecord): TableRow => ({
  id: record.record_id,
  cells: [
    { value: <span className="font-mono text-xs">{record.record_id}</span> },
    {
      value: record.extract?.last_modified_in_source_at ? (
        <Timestamp timestamp={record.extract.last_modified_in_source_at} />
      ) : (
        "—"
      ),
    },
    { value: record.extract?.error?.error_code ?? "—" },
  ],
});

const ExtractRecordsTable = () => {
  const { extract, dataset, isDatasetLoading } = useExtractContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // Default to has_error=true on first load
  useEffect(() => {
    if (!searchParams.has("has_error")) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("has_error", "true");
          return params;
        },
        { replace: true },
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasErrorParam = searchParams.get("has_error");
  const errored = extract.records_errored ?? 0;
  const processed = extract.records_processed ?? 0;

  const emptyMessage =
    hasErrorParam === "true" && errored === 0 && processed > 0 ? "No errors found." : "No records found.";

  const useQuery = (params: { pageSize?: number }, filters?: Record<string, string>) =>
    useExtractDebug(extract.id, params, filters);

  if (isDatasetLoading) {
    return <Spinner />;
  }

  // Datalake keeps per-record history for dimension tables only; fact datasets are rejected.
  if (dataset?.type === "fact") {
    return <Alert kind={AlertKind.INFO}>Record debugging is only available for dimension datasets.</Alert>;
  }

  return (
    <ResourceTable<ExtractDebugRecord>
      columns={COLUMNS}
      useQuery={useQuery}
      rowMapper={mapRecordToRow}
      emptyMessage={emptyMessage}
      filterFields={FILTER_FIELDS}
      loadingMessage="Loading records from the datalake. This process may take a while."
    />
  );
};

export default ExtractRecordsTable;
